// CAD symbol library — coordinates extracted from reference DXF.
// All coords are in "symbol units" (world space, y-up).
// IT and Main Entrance: outer circle r=50, normalized from DXF r=83.266 (scale ≈ 0.6005)
// Kitchen: outer rectangle ±25×±50, normalized from DXF 115.92×229.22 (scale ≈ 0.4363)
// Use instantiateSymbol(sym, cx, cy, scale) to place at world position.

// ── Arc helper ─────────────────────────────────────────────────────────────────
// Generates polyline points for an arc, counterclockwise from startDeg to endDeg.
function arcPts(cx, cy, r, startDeg, endDeg, steps = 24) {
  const pts = []
  for (let i = 0; i <= steps; i++) {
    const a = ((startDeg + (endDeg - startDeg) * i / steps) * Math.PI) / 180
    pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r])
  }
  return pts
}

// ── Symbol definitions ─────────────────────────────────────────────────────────
export const SYMBOLS = [
  // ── IT ──────────────────────────────────────────────────────────────────────
  // Outer circle r=50 with "I" (left) and "T" (right) letterforms.
  // Extracted from DXF entities 47EBE, 47EC1–47EC3, 47ECD, 47ECE.
  {
    id: 'it',
    name: 'IT',
    entities: [
      // Outer circle
      { type: 'CIRCLE', center: [0, 0], radius: 50 },

      // Left "I" — stem + top serif + bottom serif
      { type: 'LINE', start: [-17.7,  19.8], end: [-17.7, -19.8] },   // stem
      { type: 'LINE', start: [-21.7,  19.8], end: [-13.7,  19.8] },   // top serif
      { type: 'LINE', start: [-21.7, -19.8], end: [-13.7, -19.8] },   // bottom serif

      // Right "T" — wide top bar + stem (no bottom serif)
      { type: 'LINE', start: [2.0,  19.8], end: [26.3,  19.8] },      // top bar
      { type: 'LINE', start: [14.1, 19.8], end: [14.1, -19.8] },      // stem
    ],
  },

  // ── Main Entrance ────────────────────────────────────────────────────────────
  // Outer circle r=50 with a standard floor-plan door swing symbol.
  // Extracted from DXF entities 47EC5–47EC9.
  // Door opens 90° counterclockwise (arc center at right jamb top, r=47.7).
  {
    id: 'main_entrance',
    name: 'Main Entrance',
    entities: [
      // Outer circle
      { type: 'CIRCLE', center: [0, 0], radius: 50 },

      // Right door jamb (wall-side step detail)
      { type: 'LWPOLYLINE', closed: false, points: [
        [23.1, -13.2], [23.1, -20.1], [19.9, -20.1],
        [19.9, -15.9], [21.5, -15.9], [21.5,  34.4],
      ]},

      // Door leaf (thin slab)
      { type: 'LWPOLYLINE', closed: false, points: [
        [18.3, -13.2], [18.3, 34.4], [21.5, 34.4],
      ]},

      // Left door jamb (closed step)
      { type: 'LWPOLYLINE', closed: true, points: [
        [-28.3, -13.2], [-28.3, -20.1], [-24.6, -20.1],
        [-24.6, -15.9], [-26.2, -15.9], [-26.2, -13.2],
      ]},

      // Door swing arc — counterclockwise 90° from top (door closed) to left (door open)
      // center=(21.5, -13.2), r=47.7, 90°→180°
      { type: 'LWPOLYLINE', closed: false, points: arcPts(21.5, -13.2, 47.7, 90, 180, 24) },
    ],
  },

  // ── Kitchen ──────────────────────────────────────────────────────────────────
  // Rectangular outer boundary (no circle) with upper counter shelf and left sink.
  // Extracted from DXF entities 47ECA–47ECC.
  // Normalized: outer rect 50.6×100, scale = 50/114.61 ≈ 0.4363
  {
    id: 'kitchen',
    name: 'Kitchen',
    entities: [
      // Outer kitchen boundary rectangle
      { type: 'LWPOLYLINE', closed: true, points: [
        [-25.3, -50], [25.3, -50], [25.3, 50], [-25.3, 50],
      ]},

      // Upper counter / shelf (inner rectangle at top)
      { type: 'LWPOLYLINE', closed: true, points: [
        [-19.0, 28.0], [18.0, 28.0], [18.0, 43.6], [-19.0, 43.6],
      ]},

      // Sink / drain circle (left-middle)
      { type: 'CIRCLE', center: [-14.1, 10.5], radius: 5.3 },
    ],
  },
]

// ── Instantiate ────────────────────────────────────────────────────────────────
// Translate and scale a symbol's entities to world coordinates.
// scale: multiply symbol units → world units (e.g. drawingSize / 100).
export function instantiateSymbol(sym, cx, cy, scale) {
  return sym.entities.map(e => {
    if (e.type === 'LWPOLYLINE') {
      return {
        type: 'LWPOLYLINE',
        points: e.points.map(([x, y]) => [cx + x * scale, cy + y * scale]),
        closed: e.closed,
      }
    }
    if (e.type === 'LINE') {
      return {
        type: 'LINE',
        start: [cx + e.start[0] * scale, cy + e.start[1] * scale],
        end:   [cx + e.end[0]   * scale, cy + e.end[1]   * scale],
      }
    }
    if (e.type === 'CIRCLE') {
      return {
        type: 'CIRCLE',
        center: [cx + e.center[0] * scale, cy + e.center[1] * scale],
        radius: e.radius * scale,
      }
    }
    return null
  }).filter(Boolean)
}
